// Cyber Schooling - Main Application Entry Point
// This is a consolidated main.js - see src/main-extracted.js for the full extracted code

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/'
            });
            console.log('Service Worker registered:', registration.scope);

            // Check for updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New version available
                        console.log('New version available! Refresh to update.');
                        if (window.showToast) {
                            window.showToast('New version available. Refresh the page to update.', 'info');
                        }
                    }
                });
            });
        } catch (error) {
            console.log('Service Worker registration failed:', error);
        }
    });
}

// Import stylesheets
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/modals.css';
import './styles/animations.css';
import './styles/effects.css';
import './styles/apps.css'; // New styles
import './styles/auth.css'; // Authentication modal styles

// Import and initialize tenant system BEFORE i18n
import { getCurrentTenant } from './tenant.js';

// Initialize tenant detection first
const tenant = getCurrentTenant();
console.log('Running on tenant:', tenant.name);

// Apply tenant-specific theme
document.documentElement.style.setProperty('--accent-color', tenant.theme.accentColor);

// Import localization
import './i18n.js';
import './translations.js';

// Import modules
import { showToast, parseErrorMessage } from './modules/toast.js';
import { createConfetti } from './modules/confetti.js';
import { initSettings } from './modules/settings.js';
import { handleAIFunctionCall } from './modules/settings/ai-control.js';
import { initBackgroundPicker } from './modules/settings/background.js';
import { initHeader } from './modules/header.js';
import { initAuth, isUserSignedIn, getToken, loginWithMagicLink } from './modules/auth.js';
import { switchLanguage } from './modules/settings/language.js';
import { initApps } from './modules/apps.js'; // New module
import { initChatHistory, openChatHistory } from './modules/chat-history.js';
import { CONFIG } from './config.js';

// Import main extracted logic (WebSocket, UI, etc.)
import './main-extracted.js';

console.log('Cyber Schooling - Vite Build Initialized');
console.log('CONFIG:', CONFIG);

// Make functions globally available for HTML onclick handlers and external access
window.showToast = showToast;
window.createConfetti = createConfetti;
window.handleAIFunctionCall = handleAIFunctionCall;
window.initBackgroundPicker = initBackgroundPicker;
window.isUserSignedIn = isUserSignedIn;
window.getAuthToken = getToken;
window.getTenant = getCurrentTenant;
window.openChatHistory = openChatHistory;

// Initialize settings module
initSettings();

// Initialize background picker on page load
initBackgroundPicker();

// Initialize header
initHeader();

// Initialize Auth
initAuth().then(() => {
    handleUrlHooks();
});

// Initialize Apps
initApps();

// Initialize Chat History
initChatHistory();

/**
 * Handle URL parameters for "hooks"
 * - lang: Switch language (e.g., ?lang=en)
 * - action: Trigger actions like login/register (e.g., ?action=register)
 * - token: Magic link login token (e.g., ?token=abc123xyz...)
 */
function handleUrlHooks() {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get('lang');
    const magicToken = urlParams.get('token');

    // 1. Handle Language Switch
    if (lang) {
        console.log(`URL Hook: Switching language to ${lang}`);
        switchLanguage(lang);
    }

    // 2. Handle Magic Link Login (priority over other login methods)
    if (magicToken) {
        console.log('URL Hook: Magic Link token detected, attempting login...');
        handleMagicLinkLogin(magicToken, urlParams);
        return; // Don't process other login hooks when magic link is present
    }

    // Note: Login/Register actions and username/password URL hooks removed
    // Authentication is now only possible via magic link tokens
}

/**
 * Handle Magic Link login flow
 * @param {string} token - The magic link token
 * @param {URLSearchParams} urlParams - The URL parameters object
 */
async function handleMagicLinkLogin(token, urlParams) {
    // Show loading indicator
    showMagicLinkLoadingOverlay();

    const result = await loginWithMagicLink(token);

    // Hide loading indicator
    hideMagicLinkLoadingOverlay();

    // Clean token from URL regardless of success/failure
    urlParams.delete('token');
    const newQuery = urlParams.toString();
    const newPath = window.location.pathname + (newQuery ? '?' + newQuery : '');
    window.history.replaceState({}, document.title, newPath);

    if (result.success) {
        console.log('URL Hook: Magic Link login successful');
        if (window.showToast) {
            window.showToast(
                window.i18n?.t('auth.magicLinkSuccess') || 'Welcome! You have been successfully logged in.',
                'success'
            );
        }
        // Reload to initialize app with authenticated state
        window.location.reload();
    } else {
        console.error('URL Hook: Magic Link login failed:', result.error);
        if (window.showToast) {
            window.showToast(result.error, 'error');
        }
        // No fallback login modal - magic link is the only login method
    }
}

/**
 * Show loading overlay during magic link processing
 */
function showMagicLinkLoadingOverlay() {
    let overlay = document.getElementById('magicLinkLoadingOverlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'magicLinkLoadingOverlay';
        overlay.className = 'magic-link-loading-overlay';
        overlay.innerHTML = `
            <div class="magic-link-loading-content">
                <div class="magic-link-spinner"></div>
                <p data-i18n="auth.magicLinkProcessing">${window.i18n?.t('auth.magicLinkProcessing') || 'Processing login...'}</p>
            </div>
        `;
        document.body.appendChild(overlay);
    }
    overlay.style.display = 'flex';
}

/**
 * Hide the magic link loading overlay
 */
function hideMagicLinkLoadingOverlay() {
    const overlay = document.getElementById('magicLinkLoadingOverlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
}



